/**
 * Order route helpers — canonical path generation with UUID validation
 * Prevents literal `/admin/orders/:orderId` and invalid-ID navigation bugs.
 */

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Validate that a value is a well-formed UUID string.
 */
export function isValidUUID(id: unknown): id is string {
  return typeof id === 'string' && id.length > 0 && UUID_REGEX.test(id);
}

/**
 * Build `/admin/orders/${orderId}` only for valid UUIDs.
 * Returns `null` for undefined / null / '' / non-UUID / literal ':orderId'.
 */
export function adminOrderDetailPath(orderId: unknown): string | null {
  return isValidUUID(orderId) ? `/admin/orders/${orderId}` : null;
}

/**
 * Build `/admin/orders/${orderId}/contract` with optional query string.
 * Returns `null` for invalid orderId.
 */
export function adminOrderContractPath(
  orderId: unknown,
  query?: string | URLSearchParams,
): string | null {
  if (!isValidUUID(orderId)) return null;
  const base = `/admin/orders/${orderId}/contract`;
  if (!query) return base;
  const qs = typeof query === 'string' ? query : query.toString();
  return qs ? `${base}?${qs}` : base;
}

/**
 * Safe navigate wrapper — returns the resolved path or fallback.
 * Useful when you need a guaranteed string for `navigate()`.
 */
export function safeAdminOrderDetailPath(
  orderId: unknown,
  fallback = '/admin/orders',
): string {
  return adminOrderDetailPath(orderId) ?? fallback;
}

export function safeAdminOrderContractPath(
  orderId: unknown,
  query?: string | URLSearchParams,
  fallback = '/admin/orders',
): string {
  return adminOrderContractPath(orderId, query) ?? fallback;
}